"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState, useEffect } from "react";
import { Search, LayoutGrid, List, X } from "lucide-react";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

interface ProductFilterProps {
  categories: string[];
}

const colorMap: Record<string, string> = {
  Powder: "bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100 active:bg-cyan-200",
  "Fresh Herbs": "bg-brand-50 text-brand-700 border-brand-200 hover:bg-brand-100 active:bg-brand-200",
  Extracts: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 active:bg-purple-200",
  Beans: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 active:bg-amber-200",
  Leaves: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100 active:bg-teal-200",
};

const colorMapActive: Record<string, string> = {
  Powder: "bg-cyan-600 text-white border-cyan-600",
  "Fresh Herbs": "bg-brand-600 text-white border-brand-600",
  Extracts: "bg-purple-600 text-white border-purple-600",
  Beans: "bg-amber-600 text-white border-amber-600",
  Leaves: "bg-teal-600 text-white border-teal-600",
};

export default function ProductFilter({ categories }: ProductFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { locale } = useLocale();
  const selectedCategory = searchParams.get("category") || "";
  const currentSearch = searchParams.get("search") || "";
  const currentView = (searchParams.get("view") || "grid") as "grid" | "list";
  const currentSort = searchParams.get("sort") || "";

  const [searchInput, setSearchInput] = useState(currentSearch);

  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      router.push(`/products?${params.toString()}`);
    },
    [router, searchParams]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      updateParam("search", searchInput || null);
    },
    [searchInput, updateParam]
  );

  return (
    <div className="mb-8 space-y-4">
      {/* Search + Sort + View Toggle */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <form onSubmit={handleSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t(locale, "products.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none bg-white"
          />
        </form>

        <select
          value={currentSort}
          onChange={(e) => updateParam("sort", e.target.value || null)}
          className="w-full sm:w-auto py-2.5 sm:py-2 pl-3 pr-8 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none bg-white appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%239ca3af%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_8px_center] bg-no-repeat"
        >
          <option value="">{t(locale, "products.sortDefault")}</option>
          <option value="name">{t(locale, "products.sortName")}</option>
          <option value="newest">{t(locale, "products.sortNewest")}</option>
          <option value="moq">{t(locale, "products.sortMoq")}</option>
        </select>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 self-start sm:self-center">
          <button
            type="button"
            onClick={() => updateParam("view", "grid")}
            className={`p-1.5 rounded-md transition-colors ${
              currentView === "grid"
                ? "bg-white shadow-sm text-brand-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            title={t(locale, "products.gridView")}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => updateParam("view", "list")}
            className={`p-1.5 rounded-md transition-colors ${
              currentView === "list"
                ? "bg-white shadow-sm text-brand-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            title={t(locale, "products.listView")}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Category Pills + Active filter */}
      <div className="flex items-center flex-wrap gap-2">
        {categories.map((category) => {
          const isActive = selectedCategory === category;
          return (
            <button
              key={category}
              onClick={() => updateParam("category", isActive ? null : category)}
              className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all ${
                isActive
                  ? colorMapActive[category] || "bg-brand-600 text-white border-brand-600"
                  : colorMap[category] || "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
              }`}
            >
              {category}
            </button>
          );
        })}

        {(selectedCategory || currentSearch) && (
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (currentView !== "grid") params.set("view", currentView);
              if (currentSort) params.set("sort", currentSort);
              router.push(`/products?${params.toString()}`);
            }}
            className="text-xs font-medium text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1 ml-1"
          >
            <X className="w-3 h-3" />
            {t(locale, "products.clearFilter")}
          </button>
        )}
      </div>
    </div>
  );
}
